import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useScopedQueryKey } from '../../hooks/useScopedQueryKey';
import { api, apiEndpoints } from '../../utils/apiService';
import type {
  Checklist,
  DocumentsPage,
  ForwardingStatus,
  TestFeedback,
  TestPage,
} from './onboardingChecklist';
import { limitsNav, showsGettingStarted } from './onboardingChecklist';

const STALE_MS = 60_000;
const SAVE_FAILED = 'That didn’t save. Try again.';

/** The server's reason when it gives one ("Add the number guests call…"). */
async function failure(response: Response | null | undefined) {
  try {
    const body = (await response?.json()) as { detail?: unknown } | undefined;
    if (typeof body?.detail === 'string' && body.detail) return body.detail;
  } catch {
    // Not JSON; fall back to the generic message.
  }
  return SAVE_FAILED;
}

async function fetchChecklist(): Promise<Checklist> {
  const response = await api.get(apiEndpoints.onboardingChecklist());
  if (!response?.ok) throw new Error('Could not load your setup checklist.');
  return (await response.json()) as Checklist;
}

/**
 * The current store's onboarding checklist. Shared by the Getting started
 * page, the dashboard card and the navigation, so one request serves all three.
 * Pass `fresh` where the store acts on it: items finish on other pages (hours,
 * documents, integrations), so a cached copy can be out of date on arrival.
 */
export function useOnboardingChecklist({ fresh = false } = {}) {
  const { user } = useAuth();
  const key = useScopedQueryKey('onboarding-checklist');
  return useQuery({
    queryKey: key,
    queryFn: fetchChecklist,
    enabled: Boolean(user),
    staleTime: STALE_MS,
    refetchOnMount: fresh ? 'always' : true,
    retry: false,
  });
}

/** The navigation's checklist gates, as last answered for this store. */
interface NavFlags {
  setupOnly: boolean;
  showSetup: boolean;
}

const rememberedNavFlags = (storageKey: string): NavFlags | null => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<NavFlags>;
    return {
      setupOnly: Boolean(parsed.setupOnly),
      showSetup: Boolean(parsed.showSetup),
    };
  } catch {
    return null;
  }
};

/**
 * `limitsNav` and `showsGettingStarted` for the navigation, remembered per
 * user and store so the first paint matches the last visit instead of
 * rendering the full nav and condensing once the checklist arrives. The
 * remembered copy is only a hint while the query is in flight; the fresh
 * answer replaces it, and is saved, as soon as it lands.
 */
export function useOnboardingNavFlags(): NavFlags {
  const key = useScopedQueryKey('onboarding-checklist');
  const storageKey = `onboarding-nav-flags:${key.join(':')}`;
  const { data } = useOnboardingChecklist();
  const loaded = Boolean(data);
  const setupOnly = limitsNav(data);
  const showSetup = showsGettingStarted(data);
  useEffect(() => {
    if (!loaded) return;
    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify({ setupOnly, showSetup }),
      );
    } catch {
      // Private windows and blocked storage: the hint just isn't kept.
    }
  }, [loaded, storageKey, setupOnly, showSetup]);
  if (loaded) return { setupOnly, showSetup };
  return (
    rememberedNavFlags(storageKey) ?? { setupOnly: false, showSetup: false }
  );
}

/**
 * The store confirms its part of an item (`confirm`); for `go_live` that is
 * the store saying it's ready for calls to reach its agent. The response is
 * the updated checklist.
 */
export function useConfirmChecklistItem() {
  const client = useQueryClient();
  const key = useScopedQueryKey('onboarding-checklist');
  return useMutation({
    mutationFn: async (itemKey: string) => {
      const response = await api.post(
        apiEndpoints.onboardingChecklistItem(itemKey),
        { action: 'confirm' },
      );
      if (!response?.ok) throw new Error('That didn’t save. Try again.');
      return (await response.json()) as Checklist;
    },
    onSuccess: (checklist) => client.setQueryData(key, checklist),
  });
}

/**
 * Opening hours with nothing set: look the store up on Google and copy its
 * listed hours for the store to check. Best effort; `found` says whether any
 * were copied.
 */
export function useHoursLookup() {
  const client = useQueryClient();
  const checklistKey = useScopedQueryKey('onboarding-checklist');
  const hoursKey = useScopedQueryKey('store-hours');
  return useMutation({
    mutationFn: async () => {
      const response = await api.post(apiEndpoints.onboardingHoursLookup());
      if (!response?.ok) return false;
      return Boolean(((await response.json()) as { found?: boolean }).found);
    },
    onSuccess: (found) => {
      if (!found) return;
      void client.invalidateQueries({ queryKey: hoursKey });
      void client.invalidateQueries({ queryKey: checklistKey });
    },
  });
}

async function fetchDocuments(): Promise<DocumentsPage> {
  const response = await api.get(apiEndpoints.onboardingDocuments());
  if (!response?.ok) throw new Error('Could not load your documents.');
  return (await response.json()) as DocumentsPage;
}

export const FAQ_CHECK_POLL_MS = 2_000;

/**
 * The Documents step. Opening it also copies in the files from the store's
 * onboarding form, the first time. Polls while an FAQ file is being checked,
 * and refreshes the checklist when the check ends.
 */
export function useOnboardingDocuments() {
  const { user } = useAuth();
  const client = useQueryClient();
  const key = useScopedQueryKey('onboarding-documents');
  const checklistKey = useScopedQueryKey('onboarding-checklist');
  const query = useQuery({
    queryKey: key,
    queryFn: fetchDocuments,
    enabled: Boolean(user),
    refetchOnMount: 'always',
    refetchInterval: (q) =>
      q.state.data?.faq_check?.status === 'checking'
        ? FAQ_CHECK_POLL_MS
        : false,
    retry: false,
  });
  const checking = query.data?.faq_check?.status === 'checking';
  const wasChecking = useRef(checking);
  useEffect(() => {
    if (wasChecking.current && !checking) {
      void client.invalidateQueries({ queryKey: checklistKey });
    }
    wasChecking.current = checking;
  }, [checking, client, checklistKey]);
  return query;
}

/** Why an upload was refused: the server's reason for the file, if any. */
async function uploadFailure(response: Response | null | undefined) {
  try {
    const body = (await response?.json()) as
      | { file?: unknown; detail?: unknown }
      | undefined;
    const reason = Array.isArray(body?.file) ? body?.file[0] : body?.detail;
    if (typeof reason === 'string' && reason) return reason;
  } catch {
    // Not JSON; fall back to the generic message.
  }
  return 'That didn’t upload. Try again.';
}

/**
 * Uploading or removing a document, and saving the FAQ link. Each answers
 * with the step's new list, and the checklist follows, since the FAQs finish
 * the step.
 */
export function useDocumentActions() {
  const client = useQueryClient();
  const key = useScopedQueryKey('onboarding-documents');
  const checklistKey = useScopedQueryKey('onboarding-checklist');
  const settle = (page: DocumentsPage) => {
    client.setQueryData(key, page);
    void client.invalidateQueries({ queryKey: checklistKey });
  };
  const upload = useMutation({
    mutationFn: async ({ file, faq }: { file: File; faq: boolean }) => {
      const form = new FormData();
      form.append('file', file);
      if (faq) form.append('faq', 'true');
      const response = await api.postForm(
        apiEndpoints.onboardingDocuments(),
        form,
      );
      if (!response?.ok) throw new Error(await uploadFailure(response));
      return (await response.json()) as DocumentsPage;
    },
    onSuccess: settle,
  });
  const remove = useMutation({
    mutationFn: async (id: number) => {
      const response = await api.delete(apiEndpoints.onboardingDocument(id));
      if (!response?.ok) throw new Error(await failure(response));
      return (await response.json()) as DocumentsPage;
    },
    onSuccess: settle,
  });
  const setFaqUrl = useMutation({
    mutationFn: async (url: string) => {
      const response = await api.put(apiEndpoints.onboardingFaqUrl(), { url });
      if (!response?.ok) throw new Error(await failure(response));
      return (await response.json()) as DocumentsPage;
    },
    onSuccess: settle,
  });
  return { upload, remove, setFaqUrl };
}

export type GoLiveStep =
  | { action: 'confirm'; main_number?: string }
  | { action: 'start_check'; test_caller_number?: string }
  | {
      action:
        | 'stop_check'
        | 'accept_number'
        | 'reject_number'
        | 'accept_call'
        | 'reject_call';
    };

/**
 * A go-live step on the Go live screen. `confirm` (with `main_number` when
 * none is on file) answers with the updated checklist. The forwarding-check
 * steps answer with the forwarding status, which replaces the polled copy
 * at once, so the screen never shows the state from before the step.
 */
export function useGoLiveAction() {
  const client = useQueryClient();
  const key = useScopedQueryKey('onboarding-checklist');
  const forwardingKey = useScopedQueryKey('onboarding-forwarding');
  return useMutation({
    mutationFn: async (step: GoLiveStep) => {
      const response = await api.post(
        apiEndpoints.onboardingChecklistItem('go_live'),
        step,
      );
      if (!response?.ok) throw new Error(await failure(response));
      return (await response.json()) as Checklist | ForwardingStatus;
    },
    onSuccess: (data, step) => {
      if (step.action === 'confirm') {
        client.setQueryData(key, data as Checklist);
        void client.invalidateQueries({ queryKey: forwardingKey });
        return;
      }
      const status = data as ForwardingStatus;
      client.setQueryData(forwardingKey, status);
      client.setQueryData<Checklist>(key, (current) =>
        current ? { ...current, forwarding: status.forwarding } : current,
      );
      // Going Live changes the whole checklist (status, counts).
      if (status.live_at) void client.invalidateQueries({ queryKey: key });
    },
  });
}

async function fetchForwarding(): Promise<ForwardingStatus> {
  const response = await api.get(apiEndpoints.onboardingForwarding());
  if (!response?.ok) throw new Error('Could not check forwarding.');
  return (await response.json()) as ForwardingStatus;
}

export const FORWARDING_POLL_MS = 5_000;

/**
 * The Go live screen's forwarding check. Polls on its own while a check is
 * running and the store isn't live yet; starting a check refetches it.
 */
export function useForwardingStatus() {
  const { user } = useAuth();
  const key = useScopedQueryKey('onboarding-forwarding');
  return useQuery({
    queryKey: key,
    queryFn: fetchForwarding,
    enabled: Boolean(user),
    refetchInterval: (query) => {
      const data = query.state.data;
      return data?.forwarding.checking && !data.live_at
        ? FORWARDING_POLL_MS
        : false;
    },
    retry: false,
  });
}

export const TEST_POLL_MS = 15_000;

async function fetchTestPage(): Promise<TestPage> {
  const response = await api.get(apiEndpoints.onboardingTest());
  if (!response?.ok) throw new Error('Could not load your test calls.');
  return (await response.json()) as TestPage;
}

/**
 * Test your agent. Refreshed every few seconds and on focus, since the store
 * is on the phone with its agent in between and its calls should appear.
 */
export function useTestPage() {
  const { user } = useAuth();
  const key = useScopedQueryKey('onboarding-test');
  return useQuery({
    queryKey: key,
    queryFn: fetchTestPage,
    enabled: Boolean(user),
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: TEST_POLL_MS,
    retry: false,
  });
}

/** "Sounds right": the store confirms its test, which needs a test call. */
export function useConfirmTested() {
  const client = useQueryClient();
  const key = useScopedQueryKey('onboarding-checklist');
  const testKey = useScopedQueryKey('onboarding-test');
  return useMutation({
    mutationFn: async () => {
      const response = await api.post(
        apiEndpoints.onboardingChecklistItem('test'),
        { action: 'confirm' },
      );
      if (!response?.ok) throw new Error(await failure(response));
      return (await response.json()) as Checklist;
    },
    onSuccess: (checklist) => {
      client.setQueryData(key, checklist);
      void client.invalidateQueries({ queryKey: testKey });
    },
  });
}

/** "Something's off": what should change, for the onboarding lead. */
export function useSendTestFeedback() {
  const client = useQueryClient();
  const testKey = useScopedQueryKey('onboarding-test');
  return useMutation({
    mutationFn: async (body: { message: string; call_id?: string }) => {
      const response = await api.post(
        apiEndpoints.onboardingTestFeedback(),
        body,
      );
      if (!response?.ok) throw new Error(await failure(response));
      return (await response.json()) as TestFeedback;
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: testKey }),
  });
}

/**
 * Connect with Stripe. `start` resolves to Stripe's onboarding URL; `refresh`
 * (back from Stripe) to the updated checklist.
 */
export function useStripeConnect() {
  const client = useQueryClient();
  const key = useScopedQueryKey('onboarding-checklist');
  const start = useMutation({
    mutationFn: async () => {
      const response = await api.post(apiEndpoints.onboardingStripe(), {
        action: 'start',
      });
      if (!response?.ok) throw new Error(await failure(response));
      return ((await response.json()) as { url: string }).url;
    },
  });
  const refresh = useMutation({
    mutationFn: async () => {
      const response = await api.post(apiEndpoints.onboardingStripe(), {
        action: 'refresh',
      });
      if (!response?.ok) throw new Error(await failure(response));
      return (await response.json()) as Checklist;
    },
    onSuccess: (checklist) => client.setQueryData(key, checklist),
  });
  return { start, refresh };
}
